import type { TPlan } from "@/core/environment/types";
import { ComparePlansDialog } from "@/dialogs/compare-plans-dialog";
import { useViewPaymentsTimestamps } from "@/features/account-management";
import { track as trackAnalytics } from "@/features/analytics";
import { useEarlyBirdsCounter } from "@/shared";
import { EARLY_BIRD_PLANS } from "@/shared/constants";
import { INTEGRATIONS } from "@/shared/data";
import type { TCRMIntegration, TMarketingIntegration } from "@/shared/types";
import { useGetMarketingIntegrationsQuery } from "@api/static-assets";
import Icon from "@components/ui/icon";
import Switch from "@components/ui/switch";
import Tooltip from "@components/ui/tooltip";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { closeDialog, openDialog } from "@store/dialogs";
import { motion } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FEATURES } from "../constants";
import { useInitiatePaddle } from "../hooks";
import { usePurchaseSubscriptionStoreShallow } from "../store";
import "../styles/index.scss";
import { getSelectedPlan } from "../utils";
import CheckoutDetails from "./checkout-details";
import EarlyBirdsCounter from "./early-birds-counter";

const PurchaseSubscriptionDialog = ({
  plan = "pro",
  period = "annually",
  source,
}: {
  plan: string;
  period: string;
  source?: string;
}) => {
  "use no memo";
  useViewPaymentsTimestamps();
  const { isEarlyBirds, count } = useEarlyBirdsCounter();
  const isPricingPlage = useRef(window.location.pathname.includes("/plans"));
  const dispatch = useAppDispatch();
  const [isYearly, setIsYearly] = useState(period === "annually");
  const { initiatePaddle } = useInitiatePaddle();
  const { isCheckoutStep, reset } = usePurchaseSubscriptionStoreShallow(
    (s) => ({
      isCheckoutStep: s.isCheckoutStep,
      reset: s.reset,
    }),
  );
  const navigate = useNavigate();

  const withEarlyBirdsDiscount = useMemo(() => {
    return EARLY_BIRD_PLANS.includes(plan) && isEarlyBirds;
  }, [plan, isEarlyBirds]);

  const data = useMemo(() => {
    const monthly = getSelectedPlan("monthly", plan) as TPlan;
    const yearly = getSelectedPlan(
      "annually",
      plan,
      withEarlyBirdsDiscount,
    ) as TPlan;
    return {
      monthly,
      yearly,
    };
  }, [period, plan, withEarlyBirdsDiscount]);

  const track = useCallback(
    (event: string, payload: Record<string, unknown> = {}) => {
      trackAnalytics(event, {
        plan: plan.toLowerCase(),
        trigger: source,
        "early bird": withEarlyBirdsDiscount,
        interval: isYearly ? "yearly" : "monthly",
        ...payload,
      });
    },
    [plan, source, withEarlyBirdsDiscount, isYearly],
  );

  const close = () => {
    track("upgrade modal", {
      action: "close",
    });
    dispatch(closeDialog("purchase-subscription"));
  };

  const handleInitiatePaddle = () => {
    initiatePaddle({
      period: isYearly ? "Annually" : "Monthly",
      title: plan,
      isEarlyBirds: withEarlyBirdsDiscount,
    });
  };

  useEffect(() => {
    handleInitiatePaddle();
  }, [isYearly, plan, withEarlyBirdsDiscount]);

  useEffect(() => {
    window?.hj("event", "opened upgrade modal");
    track("view payments popup");
    track("upgrade modal", { action: "open" });
    return () => reset();
  }, [reset]);

  useEffect(() => {
    if (!isCheckoutStep) return;
    track("upgrade modal", {
      action: "continue to payment",
    });
  }, [isCheckoutStep]);

  const price = useMemo(() => {
    return isYearly ? data.yearly.price / 12 : data.monthly.price;
  }, [isYearly, data]);

  return (
    <div className="tw-relative tw-rounded-2xl tw-bg-white tw-shadow-main">
      <button
        type="button"
        onClick={close}
        className="tw-absolute tw-left-full tw-top-0 tw-flex tw-size-10 tw-translate-x-4 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-shadow-main tw-transition-opacity hover:tw-opacity-70"
      >
        <Icon icon="ri-close-line" className="tw-text-2xl tw-text-blue-500" />
      </button>
      <div
        className={cn(
          "tw-flex tw-h-[740px] tw-w-[828px]",
          plan === "starter" && "tw-h-[658px]",
        )}
      >
        <div
          className="tw-flex tw-w-[384px] tw-shrink-0 tw-flex-col tw-rounded-l-2xl tw-px-8 tw-pb-6 tw-pt-8"
          style={{
            background:
              "linear-gradient(0deg, rgba(18, 3, 62, 0.8) 0%, rgba(18, 3, 62, 0.8) 100%), linear-gradient(85deg, #610CFF 0%, #610CFF 15.08%, #7206EF 27.36%, #7306EF 37.26%, #7505F0 45.21%, #7605F1 51.65%, #7804F2 57.01%, #7B04F4 61.72%, #7D04F5 66.22%, #8003F6 70.93%, #8202F8 76.29%, #8502F9 82.73%, #8801FB 90.68%, #8A01FC 100.58%, #8D00FE 112.85%, #8F00FF 127.94%)",
          }}
        >
          {isCheckoutStep ? (
            <CheckoutDetails
              plan={plan}
              period={isYearly ? "annually" : "monthly"}
              initiatePaddle={() => {
                track("upgrade modal ", {
                  action: "checkout back",
                });
                handleInitiatePaddle();
              }}
            />
          ) : (
            <div className="tw-flex tw-flex-col">
              <div className="tw-flex tw-items-center tw-pb-6 tw-text-2xl/none tw-font-bold tw-text-white">
                <img
                  src="https://www.storydoc.com/assets/images/pricing/icon.svg"
                  alt="Storydoc icon"
                  width="28"
                  height="28"
                  className="tw-mr-3 tw-size-7"
                />
                Get Storydoc{" "}
                <span className="tw-ml-[0.5ch] tw-capitalize">{plan}</span>
              </div>
              {withEarlyBirdsDiscount && <EarlyBirdsCounter count={count} />}
              <hr className="tw-m-0 tw-h-[1px] tw-w-full tw-bg-primary/30" />
              <div className="tw-py-5">
                <div className="tw-mb-5 tw-flex tw-items-center">
                  <Switch>
                    <Switch.Toggle
                      checked={isYearly}
                      onCheckedChange={() => {
                        setIsYearly((prev) => !prev);
                        track("click payments popup change plan length");
                      }}
                      dotClassName="tw-size-3 tw-rounded-full tw-bg-blue-50"
                      className={cn(
                        "tw-mr-2 tw-flex tw-h-4 tw-w-[26px] tw-rounded-full tw-border tw-border-solid tw-border-blue-600 tw-bg-blue-700 tw-p-[1px]",
                        isYearly && "tw-border-[transparent] tw-bg-primary",
                      )}
                    />
                    <Switch.Label className="tw-font-sans tw-text-sm tw-font-medium tw-text-primary-200">
                      Save {withEarlyBirdsDiscount ? "60%" : "40%"} with yearly
                    </Switch.Label>
                  </Switch>
                </div>
                <div className="tw-flex tw-items-center">
                  {isYearly && (
                    <span className="tw-mr-2 tw-text-[40px]/none tw-font-bold tw-text-golden tw-line-through">
                      ${data.monthly.price}
                    </span>
                  )}
                  <span className="tw-mr-1 tw-font-geologica tw-text-[40px]/none tw-font-bold tw-text-white">
                    $
                    {Number.isInteger(price) ? (
                      price
                    ) : (
                      <>
                        {Math.floor(price)}
                        <sup className="-tw-top-4 tw-text-lg tw-leading-none">
                          {Math.ceil(
                            Number((price % 1).toFixed(2).substring(2)) / 10,
                          ) * 10}
                        </sup>
                      </>
                    )}
                  </span>
                  <span className="tw-text-sm/none tw-font-medium tw-text-primary-300">
                    / per month
                  </span>
                </div>
                <p className="tw-mb-0 tw-mt-4 tw-text-sm/none tw-font-semibold tw-text-white">
                  Price per user
                  {isYearly && `, billed annually $${data.yearly.price}`}
                </p>
              </div>
              <hr className="tw-m-0 tw-h-[1px] tw-w-full tw-bg-primary/30" />
              <div className="tw-mb-[10px] tw-pt-5">
                {plan === "pro" && (
                  <p className="tw-mb-4 tw-mt-0 tw-text-sm/none tw-font-semibold tw-text-white">
                    All your business needs:
                  </p>
                )}
                <ul className="tw-mb-4 tw-ml-0 tw-list-none tw-space-y-[14px] tw-pl-0">
                  {FEATURES[plan as keyof typeof FEATURES].map(
                    (feature, index) => (
                      <motion.li
                        key={index}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.15,
                          delay: index * 0.1,
                          ease: "easeOut",
                        }}
                        className="tw-flex tw-list-none tw-items-center tw-font-sans tw-text-sm/none tw-font-normal tw-text-white"
                      >
                        <Icon
                          icon="ri-check-line"
                          className="tw-mr-2 tw-text-green-500"
                        />
                        {feature}
                        {feature === "Integrations" && <IntegrationsTooltip />}
                      </motion.li>
                    ),
                  )}
                </ul>
              </div>
            </div>
          )}

          <div className="tw-mt-auto">
            <a
              href="https://www.g2.com/products/storydoc/reviews"
              rel="noopener noreferrer"
              target="_blank"
              className="tw-mb-5 tw-flex tw-w-full tw-items-center tw-justify-center"
            >
              <img
                src="https://www.storydoc.com/assets/images/pricing/dialog-g2.png"
                alt="G2"
                width="320"
                height="40"
                className="tw-h-auto tw-w-full"
              />
            </a>
            <div
              className={cn(
                "tw-flex tw-items-center tw-justify-between",
                isPricingPlage.current && "tw-justify-center",
              )}
            >
              {!isPricingPlage.current && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    dispatch(
                      openDialog({
                        id: ComparePlansDialog.id,
                        content: ComparePlansDialog,
                      }),
                    );
                    track("pricing page", {
                      action: "open",
                      from: `${plan.toLowerCase()} modal`,
                      step: source ? "features" : "payment",
                    });
                    dispatch(closeDialog("purchase-subscription"));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      dispatch(
                        openDialog({
                          id: ComparePlansDialog.id,
                          content: ComparePlansDialog,
                        }),
                      );
                      track("pricing page", {
                        action: "open",
                        from: `${plan.toLowerCase()} modal`,
                        step: source ? "features" : "payment",
                      });
                      dispatch(closeDialog("purchase-subscription"));
                    }
                  }}
                  className="tw-flex tw-cursor-pointer tw-items-center tw-font-sans tw-text-sm tw-font-medium tw-text-primary-300 tw-no-underline tw-transition-opacity hover:tw-opacity-80"
                >
                  <Icon
                    icon="ri-external-link-line"
                    className="tw-mr-1 tw-text-primary-300"
                  />
                  See other plans
                </span>
              )}
              <img
                src="https://www.storydoc.com/assets/images/pricing/payment-secure.png"
                alt="arrow"
                width="100"
                height="20"
              />
            </div>
          </div>
        </div>
        <div className="no-scrollbar tw-w-full tw-overflow-auto tw-p-5">
          <div className="paddle-checkout-container tw-w-full"></div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseSubscriptionDialog;

const IntegrationsTooltip = () => {
  const { data: integrations } = useGetMarketingIntegrationsQuery({});
  const proMarketingIntegrations = useMemo(() => {
    return integrations?.filter(
      (integration: TMarketingIntegration) => integration.plan === "pro",
    );
  }, [integrations]);

  const crmIntegrations = useMemo(() => {
    return INTEGRATIONS.filter((integration) => integration.plan === "pro");
  }, [proMarketingIntegrations]);

  return (
    <Tooltip placement="top-start">
      <Tooltip.Trigger className="tw-ml-2 tw-flex tw-items-center tw-justify-center">
        <Icon icon="ri-information-line" className="tw-text-primary-300" />
      </Tooltip.Trigger>
      <Tooltip.Content
        className={cn(
          "tw-z-[99999] tw-grid tw-max-w-64 tw-grid-cols-1 tw-rounded-lg tw-bg-blue tw-p-6",
          crmIntegrations.length > 0 &&
            "tw-max-w-[407px] tw-grid-cols-[141px_1fr]",
        )}
      >
        {crmIntegrations.length > 0 && (
          <div>
            <span className="tw-font-sans tw-text-[13px] tw-font-semibold tw-text-white">
              CRM integrations:
              <div className="tw-mt-5 tw-space-y-4">
                {crmIntegrations?.map((integration: TCRMIntegration) => (
                  <div key={integration.id} className="tw-flex tw-items-center">
                    <img
                      src={integration.logo}
                      alt={integration.title}
                      className="tw-mr-2 tw-size-4 tw-rounded tw-object-cover"
                    />
                    <span className="tw-font-sans tw-text-[13px] tw-font-normal tw-text-white">
                      {integration.title}
                    </span>
                  </div>
                ))}
              </div>
            </span>
          </div>
        )}
        <div>
          <span className="tw-font-sans tw-text-[13px] tw-font-semibold tw-text-white">
            Marketing integrations:
            <div className="tw-mt-5 tw-space-y-4">
              {proMarketingIntegrations?.map(
                (integration: TMarketingIntegration) => (
                  <div key={integration.id} className="tw-flex tw-items-center">
                    <img
                      src={integration.image}
                      alt={integration.name}
                      className="tw-mr-2 tw-size-4 tw-rounded tw-object-cover"
                    />
                    <span className="tw-font-sans tw-text-[13px] tw-font-normal tw-text-white">
                      {integration.name}
                    </span>
                  </div>
                ),
              )}
            </div>
          </span>
        </div>
      </Tooltip.Content>
    </Tooltip>
  );
};
